import { h } from 'preact'
import { useCallback, useEffect, useRef, useState } from 'preact/hooks'
import { drive_index_path } from '../../routes'
import { useBootstrapModal, csrfToken } from '../../modules/drive/bootstrap-modal.js'
import { useDriveStore, useDriveI18n, useDriveUi } from './drive-context.js'
import { RequiredAsterisk } from '../shared/RequiredAsterisk.jsx'

function numericFolderId(id) {
  if (id == null || id === '') return null
  const n = Number(id)
  return Number.isFinite(n) ? n : null
}

export function NewFolderModal({ open, onClose }) {
  const store = useDriveStore()
  const i18n = useDriveI18n()
  const ui = useDriveUi()
  const t = (key) => i18n.t(key)
  const [name, setName] = useState('')
  const [errors, setErrors] = useState([])
  const [busy, setBusy] = useState(false)
  const inputRef = useRef(null)

  const handleHidden = useCallback(() => onClose?.(), [onClose])
  const modalRef = useBootstrapModal(open, handleHidden)

  useEffect(() => {
    if (!open) return
    setName('')
    setErrors([])
    setBusy(false)
    const folder = store.currentFolder.value
    if (ui.isVirtualFolder?.(folder)) {
      onClose?.()
      window.alert(t('drive.virtual_folder_no_subfolders'))
      return
    }
    setTimeout(() => inputRef.current?.focus(), 50)
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  async function submit(e) {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) return
    setBusy(true)
    setErrors([])
    try {
      const parentId = numericFolderId(store.currentFolder.value?.id)
      const res = await fetch(drive_index_path({ format: 'json' }), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken()
        },
        body: JSON.stringify({ drive_folder: { name: trimmed, parent_id: parentId } })
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setErrors(data.errors || [t('drive.error')])
        return
      }
      await store.loadFolder(window.location.pathname + '.json' + window.location.search)
      onClose?.()
    } catch (err) {
      console.error(err)
      setErrors([t('drive.error')])
    } finally {
      setBusy(false)
    }
  }

  const parentName = store.currentFolder.value?.name

  return (
    <div class="modal fade" tabindex="-1" aria-hidden="true" ref={modalRef}>
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <form onSubmit={submit}>
            <div class="modal-header">
              <h5 class="modal-title"><i class="bi bi-folder-plus me-2" />{t('drive.new_folder')}</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label={t('helpers.close')} />
            </div>
            <div class="modal-body">
              {errors.length > 0 && (
                <div class="alert alert-danger">{errors.map((err) => <div key={err}>{err}</div>)}</div>
              )}
              <div class="mb-3">
                <label class="form-label">
                  {t('drive.folder_name')}
                  <RequiredAsterisk />
                </label>
                <input
                  ref={inputRef}
                  type="text"
                  class="form-control"
                  placeholder={t('drive.folder_name_placeholder')}
                  value={name}
                  onInput={(e) => setName(e.target.value)}
                  required
                />
              </div>
              {parentName && (
                <p class="text-muted mb-0">
                  <i class="bi bi-folder me-1" />
                  <span>{t('drive.creating_in')}: </span>
                  <strong>{parentName}</strong>
                </p>
              )}
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{t('common.cancel')}</button>
              <button type="submit" class="btn btn-primary" disabled={busy}>{t('drive.create_folder')}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
